import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { Subject } from 'rxjs';

import { EnvironmentService } from '@offconon/shared/utils/services/environment';

import { TranslateDebugService } from './translate-debug.service';

export interface TranslateWebpageRecord {
  id: number;
  translate_key: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class TranslateDebugDialogService {
  private environmentService = inject(EnvironmentService);
  private translateDebugService = inject(TranslateDebugService);
  private platformId = inject(PLATFORM_ID);
  private ngZone = inject(NgZone);
  private document = inject(DOCUMENT);

  private readonly displayedKeys = new Set<string>();
  private readonly textToKeyMap = new Map<string, string>();
  private readonly displayedValueByKey = new Map<string, { base?: string; decorated?: string }>();
  private readonly indicatorSuppressionRoots = new Set<HTMLElement>();

  readonly openRequest$ = new Subject<{ key: string; baseValue: string | null }>();

  private normalizeText(candidate: string | null | undefined): string | null {
    if (typeof candidate !== 'string') {
      return null;
    }
    const withoutNbsp = candidate.replace(/\u00a0|&nbsp;/g, ' ');
    const collapsed = withoutNbsp.replace(/\s+/g, ' ').trim();
    const lowerCased = collapsed.toLocaleLowerCase();
    return lowerCased.length ? lowerCased : null;
  }

  private expandTextCandidates(value: string | null | undefined): string[] {
    if (typeof value !== 'string') {
      return [];
    }
    const stripped = value.replace(/<[^>]+>/g, ' ');
    if (stripped === value) {
      return [value];
    }
    return [value, stripped];
  }

  registerDisplayedKey(key: string): void {
    if (!key) {
      return;
    }
    this.displayedKeys.add(key.trim());
  }

  registerDisplayedValue(
    key: string | null | undefined,
    baseValue: string | null | undefined,
    decoratedValue: string | null | undefined,
  ): void {
    if (!key) {
      return;
    }
    const trimmedKey = key.trim();
    if (!trimmedKey.length) {
      return;
    }
    const candidates = new Set<string>();
    [baseValue, decoratedValue].forEach((candidate) => {
      this.expandTextCandidates(candidate).forEach((variant) => {
        const normalized = this.normalizeText(variant);
        if (normalized) {
          candidates.add(normalized);
        }
      });
    });
    candidates.forEach((normalized) => this.textToKeyMap.set(normalized, trimmedKey));

    this.displayedValueByKey.set(trimmedKey, {
      base: baseValue ?? undefined,
      decorated: decoratedValue ?? undefined,
    });
  }

  registerIndicatorSuppressionRoot(element: HTMLElement | null | undefined): () => void {
    if (element) {
      this.indicatorSuppressionRoots.add(element);
    }
    return () => {
      if (element) {
        this.indicatorSuppressionRoots.delete(element);
      }
    };
  }

  shouldSuppressIndicatorForElement(element: HTMLElement | null | undefined): boolean {
    if (!element) {
      return false;
    }
    for (const root of this.indicatorSuppressionRoots) {
      if (root.contains(element)) {
        return true;
      }
    }
    return false;
  }

  private resolveKeyFromText(candidate: string | null | undefined): string | null {
    const normalized = this.normalizeText(candidate);
    if (!normalized) {
      return null;
    }
    const direct = this.textToKeyMap.get(normalized);
    if (direct) {
      return direct;
    }

    let bestMatch: { key: string; score: number } | null = null;
    for (const [registeredText, key] of this.textToKeyMap.entries()) {
      if (!registeredText || !normalized.includes(registeredText)) {
        continue;
      }
      const score = registeredText.length;
      if (!bestMatch || score > bestMatch.score) {
        bestMatch = { key, score };
      }
    }

    if (bestMatch) {
      return bestMatch.key;
    }

    const segments = normalized
      .split(/[\n\r\t\f]+|\s{2,}|[,|/\\-]/)
      .map((segment) => segment.trim())
      .filter(Boolean);
    for (const segment of segments) {
      const key = this.textToKeyMap.get(segment);
      if (key) {
        return key;
      }
    }

    return null;
  }

  private getDisplayedBaseValue(key: string): string | null {
    const entry = this.displayedValueByKey.get(key);
    return entry?.base ?? null;
  }

  isRegisteredKey(candidate: string | null | undefined): boolean {
    if (!candidate) {
      return false;
    }
    return this.displayedKeys.has(candidate.trim());
  }

  private toElement(target: EventTarget | null): Element | null {
    if (!target) {
      return null;
    }
    if (target instanceof Element) {
      return target;
    }
    if (target instanceof Node) {
      return target.parentElement;
    }
    return null;
  }

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    if (this.environmentService.environment?.production) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.document.addEventListener('click', this.handleDocumentClick, true);
    });
  }

  openForKey(key: string): void {
    if (this.environmentService.environment?.production) {
      return;
    }
    if (!key) {
      return;
    }

    const baseValue = this.getDisplayedBaseValue(key);
    this.openRequest$.next({ key, baseValue });
  }

  private handleDocumentClick = (event: MouseEvent) => {
    if (!this.shouldHandleClicks()) {
      return;
    }

    const targetElement = this.toElement(event.target);
    if (!targetElement) {
      return;
    }

    const composedPath = event.composedPath?.() ?? [];
    const hasModifier = event.altKey || event.ctrlKey || event.metaKey;

    // 1. Identify interactive elements
    const interactiveSelectors =
      'button, input, textarea, a, label, .p-component, [role="button"], [role="link"]';
    const isInteractive = composedPath.some((node) => {
      if (!(node instanceof Element)) {
        return false;
      }
      return node.matches(interactiveSelectors);
    });

    // 2. Extract key
    const caretKey = this.extractKeyFromCaret(event);
    const key = caretKey ?? this.extractKeyFromEvent(event, targetElement);

    const isInput =
      targetElement.tagName === 'INPUT' ||
      targetElement.tagName === 'TEXTAREA' ||
      !!targetElement.closest('input, textarea');

    const isButton =
      targetElement.tagName === 'BUTTON' ||
      targetElement.classList.contains('p-button') ||
      !!targetElement.closest('button, .p-button');

    // Link detection to avoid accidental navigation
    const isLink = targetElement.tagName === 'A' || !!targetElement.closest('a');

    // 3. Skip interactive elements if no modifier (except for inputs, buttons, or elements WITH A KEY)
    if (isInteractive && !isInput && !isButton && !key && !hasModifier) {
      return;
    }

    // Special case for links: only debug if modifier is held (to avoid navigation)
    if (isLink && !hasModifier) {
      return;
    }

    // 4. Ignore clicks happening inside the debug dialog itself or PrimeNG overlay panels
    const exclusionSelectors = [
      'offconon-translate-debug-dialog',
      '.translate-debug-dialog',
      '.p-multiselect-panel',
      '.p-multiselect',
      '.p-dropdown-panel',
      '.p-select-panel',
      '.p-overlaypanel',
      '.p-connected-overlay-container',
      '.p-datepicker',
      '.p-autocomplete-panel',
      '.p-tooltip',
      '.p-confirm-dialog',
      '.p-toast',
      '.p-multiselect-option',
    ];

    const isExcluded = composedPath.some((node) => {
      if (!(node instanceof Element)) {
        return false;
      }
      return node.closest(exclusionSelectors.join(', '));
    });

    if (isExcluded || !key) {
      return;
    }

    // 5. Intercept or allow through
    if (isInput && !hasModifier) {
      // For inputs, we open the dialog but do NOT prevent default behavior.
      // This allows the input to focus/type while still showing the debug information.
      this.ngZone.run(() => this.openForKey(key));
    } else {
      // For plain text or interactive elements with a modifier, we intercept fully.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      this.ngZone.run(() => this.openForKey(key));
    }
  };

  private extractKeyFromCaret(event: MouseEvent): string | null {
    const doc = this.document;
    if (!doc) {
      return null;
    }

    const point = this.getCaretPoint(doc, event.clientX, event.clientY);
    if (!point || point.nodeType !== Node.TEXT_NODE) {
      return null;
    }

    const textContent = point.textContent;
    return this.resolveKeyFromText(textContent);
  }

  private getCaretPoint(doc: Document, x: number, y: number): Node | null {
    const range = (doc as any).caretRangeFromPoint?.(x, y);
    if (range && range.startContainer) {
      return range.startContainer;
    }

    const position = (doc as any).caretPositionFromPoint?.(x, y);
    if (position && position.offsetNode) {
      return position.offsetNode as Node;
    }

    return null;
  }

  private shouldHandleClicks(): boolean {
    if (this.environmentService.environment?.production) {
      return false;
    }
    return !!this.translateDebugService.isDebug$.value;
  }

  private extractKeyFromEvent(event: MouseEvent, fallbackElement?: Element | null): string | null {
    const targetElement = fallbackElement ?? this.toElement(event.target);
    const composedPath = event.composedPath?.() ?? [];
    for (const node of composedPath) {
      if (!(node instanceof HTMLElement)) {
        continue;
      }
      const candidate = this.extractKeyFromElement(node);
      if (candidate) {
        return candidate;
      }
      if (node.tagName === 'BODY') {
        break;
      }
    }

    return this.extractKeyFromElement(targetElement);
  }

  private extractKeyFromElement(element: Element | EventTarget | null | undefined): string | null {
    if (!element || !(element instanceof HTMLElement)) {
      return null;
    }

    const datasetKey =
      element.dataset['translateKey'] ?? element.getAttribute?.('data-translate-key');
    if (datasetKey && this.isRegisteredKey(datasetKey)) {
      return datasetKey.trim();
    }

    const placeholder = element.getAttribute?.('placeholder');
    if (placeholder && this.isRegisteredKey(placeholder)) {
      return placeholder.trim();
    }

    const ariaLabel = element.getAttribute?.('aria-label');
    if (ariaLabel && this.isRegisteredKey(ariaLabel)) {
      return ariaLabel.trim();
    }

    const title = element.getAttribute?.('title');
    if (title && this.isRegisteredKey(title)) {
      return title.trim();
    }

    const textCandidate = element.innerText ?? null;
    if (textCandidate && this.isRegisteredKey(textCandidate)) {
      return textCandidate;
    }

    const resolved = this.resolveKeyFromText(textCandidate);
    if (resolved) {
      return resolved;
    }

    return null;
  }
}
